"use client";

import React from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/Card/Card";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip as ChartTooltip,
  ResponsiveContainer,
  CartesianGrid,
  ReferenceLine,
  Legend,
} from "recharts";
import { useTheme } from "@/context/ThemeContext";
import { PercentageOfNewCustomersByZipCodeData } from "@/modules/stochastic/features/SalesAndCustomerDashboard/api/fetchStochasticDashboardData/types";
import { generateColors } from "@/modules/stochastic/features/SalesAndCustomerDashboard/utils/chart";
import { ChartNavigationSlider } from "@/modules/stochastic/features/SalesAndCustomerDashboard/components/ChartNavigationSlider/ChartNavigationSlider";
import { ChartMetricCard } from "@/modules/stochastic/features/SalesAndCustomerDashboard/components/ChartMetricCard/ChartMetricCard";
import ChartTooltipWithColoredYears from "@/modules/stochastic/features/SalesAndCustomerDashboard/components/ChartTooltipWithColoredYears/ChartTooltipWithColoredYears";
import { ChartBarLegendWithColoredYear } from "@/modules/stochastic/features/SalesAndCustomerDashboard/components/ChartBarLegendWithColoredYear/ChartBarLegendWithColoredYear";
import { useChartPagination } from "@/modules/stochastic/features/SalesAndCustomerDashboard/hooks/useChartPagination";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Info } from "lucide-react";

type PercentageOfNewCustomersByZipCodeChartProps = {
  chartId?: string;
  initialData: PercentageOfNewCustomersByZipCodeData;
};

export const PercentageOfNewCustomersByZipCodeChart = ({
  initialData,
}: PercentageOfNewCustomersByZipCodeChartProps) => {
  if (!initialData?.items || initialData.items.length === 0) return null;

  const { theme } = useTheme();
  const {
    page,
    setPage,
    totalPages,
    currentPageItems: paginatedData,
  } = useChartPagination(initialData.items, 10);

  const { years: allYears, totalCount } = initialData;
  const colors = generateColors(allYears, theme);
  const postalCodes = initialData.items.map((d) => d.postalCode);

  const chartData = paginatedData;

  return (
    <Card className="w-full">
      <CardHeader>
        <div className="flex items-center gap-2">
          <CardTitle className="text-fontColor dark:text-light">
            Percentage of New Customers by ZIP Code
          </CardTitle>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger>
                <Info className="h-4 w-4 text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 cursor-help" />
              </TooltipTrigger>
              <TooltipContent className="max-w-xs bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-gray-100 shadow-lg">
                <p>
                  Indicates where new market entry is occurring most
                  consistently.
                </p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
          New customer volume by neighborhood
        </p>
      </CardHeader>

      <CardContent>
        <ResponsiveContainer height={500} width="100%">
          <BarChart
            data={chartData}
            margin={{ top: 20, right: 30, left: 60, bottom: 5 }}
          >
            <CartesianGrid
              stroke={theme === "dark" ? "#374151" : "#e5e7eb"}
              strokeDasharray="3 3"
            />
            <XAxis
              dataKey="postalCode"
              stroke={theme === "dark" ? "#9CA3AF" : "#4B5563"}
            />
            <YAxis
              label={{
                value: "Percentage of New Customers (%)",
                angle: -90,
                position: "insideLeft",
                offset: 0,
                fill: theme === "dark" ? "#9CA3AF" : "#4B5563",
                style: { textAnchor: "middle", fontSize: 18 },
              }}
              stroke={theme === "dark" ? "#9CA3AF" : "#4B5563"}
              tickFormatter={(value) => `${value}%`}
            />
            <ChartTooltip
              content={
                <ChartTooltipWithColoredYears
                  colors={colors}
                  labelKey="postalCode"
                  labelPrefix="ZIP:"
                  valueSuffix="%"
                />
              }
            />
            <Legend content={<ChartBarLegendWithColoredYear />} />
            <ReferenceLine stroke="#ccc" y={0} />
            {allYears.map((year) => (
              <Bar
                dataKey={year}
                fill={colors[year]}
                isAnimationActive={true}
                key={year}
                name={year}
              />
            ))}
          </BarChart>
        </ResponsiveContainer>

        <div className="mt-8">
          <div className="text-sm text-gray-500 mb-3 text-center select-none font-semibold tracking-wide">
            Navigation Overview
          </div>

          <ChartNavigationSlider
            currentPage={page}
            itemsPerPage={10}
            onPageChange={setPage}
            postalCodes={postalCodes}
            theme={theme}
            totalPages={totalPages}
          />

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-4 gap-y-6 mt-6">
            <ChartMetricCard
              label="Current View"
              value={`${page + 1} of ${totalPages}`}
            />
            <ChartMetricCard label="ZIPs Shown" value={10} />
            <ChartMetricCard label="Total ZIPs" value={totalCount} />
            <ChartMetricCard
              label="Years Tracked"
              value={`${allYears[0]}–${allYears.at(-1)}`}
            />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
